import { z } from "zod/v4";

export const conversationCreateSchema = z.object({
  title: z.string().trim().max(200).nullable().optional(),
  jurisdiction: z.object({
    countryCode: z.string().length(2).default("AR"),
    provinceCode: z.string().max(64).nullable().optional(),
    municipalityCode: z.string().max(64).nullable().optional(),
  }).default({ countryCode: "AR", provinceCode: "MZA", municipalityCode: null }),
  metadata: z.record(z.string(), z.unknown()).default({}),
});

const conversationExpedienteIdSchema = z.string().trim().regex(
  /^\d{2}-\d{8}-\d(?:\/\d+)?$/,
  "El CUIJ no tiene un formato válido.",
);

export const conversationHistoryQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(30),
  cursor: z.string().trim().max(1000).optional(),
  query: z.string().trim().max(200).optional(),
  assignedOnly: z.enum(["true", "false"]).transform(
    (value) => value === "true",
  ).optional().default(false),
  scope: z.string().trim().max(200).optional(),
  contextType: z.enum(["expediente"]).optional(),
  contextId: conversationExpedienteIdSchema.optional(),
}).refine(
  ({ contextType, contextId }) => Boolean(contextType) === Boolean(contextId),
  {
    message: "El tipo y el identificador de contexto deben enviarse juntos.",
    path: ["contextId"],
  },
);

export const conversationAssignmentSchema = z.object({
  contextType: z.literal("expediente"),
  contextId: conversationExpedienteIdSchema,
});

export const appMessageContextSchema = z.object({
  route: z.string().trim().max(1000).nullable().optional(),
  interfaceHelp: z.boolean().optional(),
  pageState: z.record(z.string(), z.unknown()).optional(),
  activeEntity: z
    .object({
      type: z.string().trim().min(1).max(64),
      id: z.string().trim().min(1).max(300),
      label: z.string().trim().max(500).nullable().optional(),
    })
    .nullable()
    .optional(),
});

const draftReferenceAuthorSchema = z.union([
  z.string().trim().min(1).max(300),
  z.object({
    literal: z.string().trim().max(300).nullable().optional(),
    name: z.string().trim().max(300).nullable().optional(),
    family: z.string().trim().max(200).nullable().optional(),
    surname: z.string().trim().max(200).nullable().optional(),
    lastName: z.string().trim().max(200).nullable().optional(),
    given: z.string().trim().max(200).nullable().optional(),
    givenNames: z.string().trim().max(200).nullable().optional(),
    firstName: z.string().trim().max(200).nullable().optional(),
  }),
]);

export const draftReferenceSchema = z.object({
  id: z.string().trim().min(1).max(1000),
  evidenceId: z.string().trim().max(200).nullable(),
  type: z.string().trim().min(1).max(40),
  style: z.enum(["apa7", "legal"]),
  label: z.string().trim().min(1).max(1000),
  locator: z.string().trim().max(500).nullable(),
  quoteText: z.string().trim().max(2500).nullable(),
  sourceId: z.string().trim().max(200).nullable(),
  source: z.object({
    title: z.string().trim().max(1000).nullable(),
    organization: z.string().trim().max(500).nullable(),
    url: z.string().trim().max(2000).nullable(),
    tribunaleaUrl: z.string().trim().max(2000).nullable(),
    official: z.boolean(),
  }),
  metadata: z.object({
    bookId: z.string().trim().max(200).nullable(),
    editionId: z.string().trim().max(200).nullable(),
    title: z.string().trim().max(1000).nullable(),
    authors: z.array(draftReferenceAuthorSchema).max(20),
    publisher: z.string().trim().max(500).nullable(),
    edition: z.string().trim().max(300).nullable(),
    isbn: z.string().trim().max(64).nullable(),
    publishedAt: z.string().trim().max(50).nullable(),
    doi: z.string().trim().max(500).nullable(),
    publicationUrl: z.string().trim().max(2000).nullable(),
    official: z.boolean(),
    validFrom: z.string().trim().max(50).nullable(),
    validTo: z.string().trim().max(50).nullable(),
  }),
  shortCitation: z.string().trim().min(1).max(1500),
  narrativeCitation: z.string().trim().min(1).max(1500),
  bibliography: z.string().trim().min(1).max(3000),
});

export const draftToolContextSchema = z.object({
  drafts: z.array(z.object({
    id: z.string().trim().min(1).max(200),
    title: z.string().trim().min(1).max(160),
    excerpt: z.string().max(800),
    updatedAt: z.string().datetime(),
  })).max(100).default([]),
  activeDraftId: z.string().trim().min(1).max(200).nullable().optional(),
  activeDraftTitle: z.string().trim().min(1).max(160).nullable().optional(),
  activeDraftIncludesSignature: z.boolean().optional(),
  references: z.array(draftReferenceSchema).max(50).default([]),
  similarDrafts: z.array(z.object({
    id: z.string().trim().min(1).max(200),
    title: z.string().trim().min(1).max(160),
    text: z.string().max(8_000),
    updatedAt: z.string().datetime(),
  })).max(3).optional(),
});

export const messageCreateSchema = z.object({
  content: z.string().trim().max(50000).default(""),
  responseMode: z.enum([
    "accessible",
    "academic",
    "specialized",
  ]).optional(),
  asOfDate: z.coerce.date().nullable().optional(),
  requestedSources: z.array(z.enum([
    "legislation",
    "doctrine",
    "jurisprudence",
    "library",
  ])).optional(),
  appContext: appMessageContextSchema.optional(),
  draftContext: draftToolContextSchema.optional(),
  attachmentIds: z.array(z.string().regex(/^[a-f\d]{24}$/i)).max(5).optional(),
  notebookIds: z.array(z.string().regex(/^[a-f\d]{24}$/i)).max(10).optional(),
  notebookScope: z.enum(["selected", "all"]).optional(),
}).refine(
  ({ content, attachmentIds }) => Boolean(content || attachmentIds?.length),
  {
    message: "Escribí un mensaje o adjuntá un audio.",
    path: ["content"],
  },
);

export const draftEditSchema = z.object({
  instruction: z.string().trim().min(1).max(2_000),
  plainText: z.string().max(50_000).default(""),
  cursorOffset: z.number().int().nonnegative().nullable().optional(),
  selectionFrom: z.number().int().nonnegative().nullable().optional(),
  selectionTo: z.number().int().nonnegative().nullable().optional(),
  selectedText: z.string().max(12_000).default(""),
  appContext: appMessageContextSchema.optional(),
  draftContext: draftToolContextSchema.optional(),
});

export const draftEditResultSchema = z.object({
  action: z.enum([
    "insert",
    "replace_selection",
    "replace_text",
    "replace_document",
    "open_chat",
  ]),
  text: z.string(),
  targetText: z.string(),
  chatPrompt: z.string().max(2_000),
  needsVerification: z.boolean(),
  usedReferenceIds: z.array(z.string().trim().min(1).max(1000)).max(50).default([]),
});

export const groundedAnswerSchema = z.object({
  answer: z.string(),
  evidenceIds: z.array(z.string()),
  claims: z.array(z.object({
    text: z.string().describe(
      "Afirmación sustantiva respaldada por evidencia documental; nunca describas aquí el proceso de búsqueda, fuentes faltantes ni controles internos.",
    ),
    evidenceIds: z.array(z.string()).min(1),
  })).describe("Afirmaciones sustantivas auditables contra los fragmentos citados."),
  attachmentReferences: z.array(z.object({
    evidenceId: z.string(),
    exactText: z.string().trim().min(1).max(1200).nullable(),
    pageNumber: z.number().int().positive().nullable(),
  })).max(10).describe(
    "Pasajes textuales y páginas físicas declarados para los archivos adjuntos utilizados; vacío cuando no se usaron adjuntos.",
  ),
  generatedContent: z.boolean(),
  abstained: z.boolean(),
  abstentionReason: z.string().nullable(),
  warning: z.string().nullable().describe(
    "Limitación metodológica basada sólo en evidenceAssessment, separada de answer y claims; null si no hace falta.",
  ),
});

export const sseEventTypes = [
  "status",
  "answer.delta",
  "citation",
  "source",
  "warning",
  "tool.started",
  "tool.completed",
  "confirmation.required",
  "completed",
  "error",
];
